/**
 * Auth hooks for Seitiate+.
 *
 * Thin adapter over the localStorage-based auth from lib/auth.ts.
 * Provides the same useAuth() shape that CES migrated components expect.
 */

import { useEffect, useState } from 'react';
import { AUTH_CHANGED_EVENT, getUser, getToken, type AuthUser } from '@/lib/auth';

export interface AuthState {
  user: {
    uid: string;
    email: string | null;
    display_name: string | null;
    is_director: boolean;
    is_system_owner: boolean;
    can_access_command_center: boolean;
    can_access_dashboard: boolean;
    can_access_workbench: boolean;
    can_edit_sot: boolean;
    can_access_architect: boolean;
    is_waitlisted?: boolean;
    tenant: {
      tenant_id: string;
      display_name: string | null;
      role: string;
      tier: string;
      status: string;
    } | null;
    tenants: Array<{
      tenant_id: string;
      display_name: string | null;
      role: string;
      tier: string;
      status: string;
    }>;
    workspaces: Array<{
      id: string;
      name: string;
      thread_type: string;
      participant_role?: string;
    }>;
  } | null;
  isAuthenticated: boolean;
}

function mapUser(raw: AuthUser | null): AuthState['user'] {
  if (!raw) return null;
  const isDir = raw.isDirector ?? false;
  const mapTenant = (item: NonNullable<AuthUser['tenant']>) => ({
    tenant_id: item.tenant_id,
    display_name: item.display_name ?? null,
    role: item.role ?? (isDir ? 'director' : 'member'),
    // Default platform tier is now "free" (Option-B re-home; was "sense").
    tier: item.tier ?? 'free',
    status: item.status ?? 'active',
  });
  const fallbackTenant = raw.tenantId
    ? { tenant_id: raw.tenantId, display_name: null, role: isDir ? 'director' : 'member', tier: 'free', status: 'active' }
    : null;
  const tenant = raw.tenant ? mapTenant(raw.tenant) : fallbackTenant;
  const tenants = raw.tenants?.length
    ? raw.tenants.map(mapTenant)
    : tenant
      ? [tenant]
      : [];
  return {
    uid: raw.uid,
    email: raw.email,
    display_name: raw.name ?? raw.email,
    is_director: isDir,
    is_system_owner: isDir,
    can_access_command_center: isDir,
    can_access_dashboard: true,
    can_access_workbench: true,
    can_edit_sot: isDir,
    can_access_architect: isDir,
    tenant,
    tenants,
    workspaces: raw.workspaces ?? [],
  };
}

export function useAuth(): AuthState {
  const [, setVersion] = useState(0);

  useEffect(() => {
    const onAuthChanged = () => setVersion((current) => current + 1);
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    window.addEventListener('storage', onAuthChanged);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
      window.removeEventListener('storage', onAuthChanged);
    };
  }, []);

  const raw = getUser();
  const token = getToken();
  return {
    user: mapUser(raw),
    isAuthenticated: !!raw && !!token,
  };
}

export function useIsDirector(): boolean {
  const { user, isAuthenticated } = useAuth();
  return isAuthenticated && (user?.is_director ?? false);
}
